<template>
  <loader :is-loading="isSubmitting" size="20" />
  <lf-dropdown
    ref="dropdownRef"
    :options="userList"
    :value="selectedUser"
    :name="`${$t('COMMON.SEARCH')} ${$t('COMMON.USER')}`"
    :search-placeholder="`${$t('COMMON.USER')}`"
    search-enabled
    :placeholder="placeholder"
    as-badge
    :is-loading="isLoading"
    :show-warning-icon="false"
    :custom-z-index="avatar ? 'z-50' : 'z-1'"
    class="assign-user-dropdown"
    @change="selectUser"
  >
    <template v-if="avatar" #toggle-button="{ toggle }">
      <lf-avatar
        v-if="selectedUser"
        :username="selectedUser"
        :size="32"
        :theme="TYPE_MAPPINGS[type].color"
        @click="toggle"
      />
      <span
        v-else
        class="cursor-pointer rounded-full px-2 py-2 hover:bg-gray-100"
        @click="toggle"
      >
        <icon-base :icon="TYPE_MAPPINGS[type].icon" />
      </span>
    </template>
  </lf-dropdown>
</template>
<script setup lang="ts">
import { reactify } from "@vueuse/core";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { USER_BADGE_COLORS, USER_COLORS } from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import {
  useAnalystsQuery,
  useClientAnalystsQuery,
  useClientFundingAdvisorsQuery,
  useClientUnderwritersQuery,
  useFundingAdvisorsQuery,
  useUnderwritersQuery,
} from "@/hooks/queries/useUsers";

import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

import type { AvatarTheme } from "@/components/ui/LFAvatar.vue";
import type { ISystemPreferences } from "@/models/applications";
import type { AssignableUser, IUser } from "@/models/users";

const { t } = useI18n();

const dropdownRef = useTemplateRef("dropdownRef");

const TYPE_MAPPINGS: Record<
  AssignableUser,
  {
    actionSelector: string;
    removeSelector: string;
    icon: string;
    color: AvatarTheme;
    isClientRole?: boolean;
  }
> = {
  underwriter: {
    actionSelector: "applications/assignUnderwriter",
    removeSelector: "applications/removeUnderwriter",
    icon: "briefcase-checked",
    color: USER_BADGE_COLORS[USER_COLORS.BLUE],
  },
  fundingAdvisor: {
    actionSelector: "applications/assignFundingAdvisor",
    removeSelector: "applications/removeFundingAdvisor",
    icon: "chat-bubble",
    color: USER_BADGE_COLORS[USER_COLORS.GREEN],
  },
  analyst: {
    actionSelector: "applications/assignAnalyst",
    removeSelector: "applications/removeAnalyst",
    icon: "pie-chart",
    color: USER_BADGE_COLORS[USER_COLORS.MINT],
  },
  clientUnderwriter: {
    actionSelector: "applications/assignClientUnderwriter",
    removeSelector: "applications/removeClientUnderwriter",
    icon: "briefcase-checked",
    color: USER_BADGE_COLORS[USER_COLORS.BLUE],
    isClientRole: true,
  },
  clientFundingAdvisor: {
    actionSelector: "applications/assignClientFundingAdvisor",
    removeSelector: "applications/removeClientFundingAdvisor",
    icon: "chat-bubble",
    color: USER_BADGE_COLORS[USER_COLORS.GREEN],
    isClientRole: true,
  },
  clientAnalyst: {
    actionSelector: "applications/assignClientAnalyst",
    removeSelector: "applications/removeClientAnalyst",
    icon: "pie-chart",
    color: USER_BADGE_COLORS[USER_COLORS.MINT],
    isClientRole: true,
  },
};

const {
  appId,
  clientId,
  modelValue = "",
  type = "fundingAdvisor",
  avatar = false,
  isExtended = false,
  loadOptionsWhenOpen,
} = defineProps<{
  modelValue?: string | number;
  appId: string;
  type?: AssignableUser;
  clientId?: string | number;
  avatar?: boolean;
  isExtended?: boolean;
  loadOptionsWhenOpen?: boolean;
}>();

const emit = defineEmits<{
  (eventName: "changed", userId: string | number): void;
}>();

const REMOVE = "remove";
const { isFundingAdvisor, isClientFundingAdvisor } = useAuth();
const store = useStore();

const isQueryEnabled = reactify((isAllowedForType: boolean) => {
  if (!loadOptionsWhenOpen) {
    return isAllowedForType;
  }

  return !!(
    isAllowedForType &&
    loadOptionsWhenOpen &&
    dropdownRef.value?.showMenu
  );
});

const { data: analystsData, isLoading: analystsLoading } = useAnalystsQuery({
  ignoreAssignedDeals: true,
  enabled: isQueryEnabled(type === "analyst"),
});
const { data: fundingAdvisorsData, isLoading: fundingAdvisorsLoading } =
  useFundingAdvisorsQuery({
    ignoreAssignedDeals: true,
    enabled: isQueryEnabled(type === "fundingAdvisor"),
  });
const { data: underwritersData, isLoading: underwritersLoading } =
  useUnderwritersQuery({
    ignoreAssignedDeals: true,
    enabled: isQueryEnabled(type === "underwriter"),
  });

const { data: clientAnalystsData, isLoading: clientAnalystsLoading } =
  useClientAnalystsQuery({
    ignoreAssignedDeals: true,
    clientId: clientId?.toString(),
    enabled: isQueryEnabled(!!clientId && type === "clientAnalyst"),
  });
const {
  data: clientFundingAdvisorsData,
  isLoading: clientFundingAdvisorsLoading,
} = useClientFundingAdvisorsQuery({
  ignoreAssignedDeals: true,
  clientId: clientId?.toString(),
  enabled: isQueryEnabled(!!clientId && type === "clientFundingAdvisor"),
});
const { data: clientUnderwritersData, isLoading: clientUnderwritersLoading } =
  useClientUnderwritersQuery({
    ignoreAssignedDeals: true,
    clientId: clientId?.toString(),
    enabled: isQueryEnabled(!!clientId && type === "clientUnderwriter"),
  });

const isSubmitting = ref(false);
const selectedUser = ref(modelValue);

const placeholder = computed(() => (isExtended ? t("COMMON.UNASSIGNED") : "-"));

const isLoading = computed(() => {
  return (
    analystsLoading.value ||
    fundingAdvisorsLoading.value ||
    underwritersLoading.value ||
    clientAnalystsLoading.value ||
    clientFundingAdvisorsLoading.value ||
    clientUnderwritersLoading.value
  );
});

const users = computed<IUser[]>(() => {
  const isClientRole =
    type === "clientAnalyst" ||
    type === "clientFundingAdvisor" ||
    type === "clientUnderwriter";

  if (isClientRole) {
    if (!clientId) {
      return [];
    }

    const clientUserDataMap = {
      clientAnalyst: clientAnalystsData.value ?? [],
      clientFundingAdvisor: clientFundingAdvisorsData.value ?? [],
      clientUnderwriter: clientUnderwritersData.value ?? [],
    };

    return clientUserDataMap[type as keyof typeof clientUserDataMap];
  }

  const userDataMap = {
    analyst: analystsData.value ?? [],
    fundingAdvisor: fundingAdvisorsData.value ?? [],
    underwriter: underwritersData.value ?? [],
  };
  const userData = userDataMap[type as keyof typeof userDataMap] || [];
  return userData;
});

const userList = computed(() => {
  const options = users.value.reduce<Record<string, string>>((res, user) => {
    if (user.id) {
      res[user.id] = user.full_name || "-";
    }
    return res;
  }, {});
  if (selectedUser.value) {
    return {
      ...options,
      [REMOVE]: t("COMMON.NONE"),
    };
  }
  return options;
});

const dealVisibilityPreferences = computed<ISystemPreferences>(
  () => store.getters["applications/systemPreferences"]
);

const currentUser = computed<IUser>(() => store.getters["auth/user"]);

async function selectUser(userId: string | number) {
  isSubmitting.value = true;
  selectedUser.value =
    (userList.value[userId] === t("COMMON.NONE")
      ? ""
      : userList.value[userId]) || "";

  const selectedUserData = users.value.find(
    (user) => user.id === Number(userId)
  );

  const payload = {
    appId: appId,
    userId,
    removeDeal: false,
    user: selectedUserData,
    ...(clientId && { clientId }),
  };

  if (
    type === "fundingAdvisor" &&
    (isFundingAdvisor || isClientFundingAdvisor) &&
    !dealVisibilityPreferences.value.deal_visibility["funding advisor"]
      ?.assigned &&
    Number(userId) !== currentUser.value.id
  ) {
    payload.removeDeal = true;
  }

  const validUserId = payload.userId !== REMOVE;
  const action = validUserId
    ? TYPE_MAPPINGS[type].actionSelector
    : TYPE_MAPPINGS[type].removeSelector;
  const actionPayload = validUserId ? payload : payload.appId;
  try {
    await store.dispatch(action, actionPayload);
    store.dispatch("applications/syncActiveToAll");
    emit("changed", userId);
  } finally {
    isSubmitting.value = false;
  }
}

watch(
  () => modelValue,
  (value) => {
    selectedUser.value = value;
  }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.assign-user-dropdown:deep() {
  .badge {
    @apply h-6 text-xxs leading-4 font-semibold uppercase;
  }
}
</style>
